import { useEffect, useRef } from 'react';
import { supabase } from './supabase';
import { log } from './logger';

/**
 * Subscribe to postgres_changes on one or more tables, debouncing refresh calls.
 * Channel names include Date.now() to prevent duplicate subscription bugs.
 */
export function useRealtimeSubscription(
  businessId: string | undefined,
  tables: string[],
  refresh: () => void,
  debounceMs = 500,
) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (!businessId || tables.length === 0) return;

    const channelName = `rt-${tables.join('-')}-${businessId.slice(0, 8)}-${Date.now()}`;

    let channel = supabase.channel(channelName);

    for (const table of tables) {
      channel = channel.on(
        'postgres_changes' as never,
        { event: '*', schema: 'public', table, filter: `business_id=eq.${businessId}` } as never,
        () => {
          // Debounce: if multiple changes arrive rapidly, only refresh once
          clearTimeout(timeoutRef.current);
          timeoutRef.current = setTimeout(refresh, debounceMs);
        },
      );
    }

    channel.subscribe((status, err) => {
      if (status !== 'SUBSCRIBED') {
        log.warn('[realtime] subscribe non-OK', { channel: channelName, status, err });
      }
    });

    return () => {
      clearTimeout(timeoutRef.current);
      void supabase.removeChannel(channel);
    };
  }, [businessId, refresh, debounceMs, tables.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps
}
